import { useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { SttDialog } from '../../sporttia/dialogs/SttDialog';
import SttValidatedForm from '../../sporttia/SttValidatedForm';
import { useLoader } from '../../lib/hooks';
import usePaymentsService from '../../services/PaymentsService';
import { AppContext } from '../../sporttia/AppContext';
import { getErrorMessage } from '../../lib/utils';

type PayMultiplePaymentsDialogProps = {
    paymentsIds: number[] | null;
    onClose: () => void;
    onCreate: (response: unknown) => void;
    userID?: number;
};

type PaymentFormData = {
    childrenConcept: string;
    childrenUserId: string;
};

type CreatePaymentParams = {
    childrenConcept: string;
    childrenUserId?: number;
    childrenPaymentIds: number[];
};

/**
 * Dialog to choose concept and user to associate the payments to be paid.
 * @param paymentsIds Payments IDs array.
 * @param onClose Function executed when closing the dialog.
 * @param onCreate Launched when a new payment is created.
 * @returns {React.ReactElement}
 */
function PayMultiplePaymentsDialog({
    paymentsIds,
    onClose,
    onCreate,
    userID,
}: PayMultiplePaymentsDialogProps): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const form = useForm<PaymentFormData>();
    const paymentsService = usePaymentsService();

    const createMutation = useMutation({
        mutationFn: (params: { params: CreatePaymentParams }) =>
            paymentsService.create(params.params),
        onSuccess: (response: unknown) => {
            const errorResponse = response as { error?: { msg?: string } };
            if (errorResponse?.error?.msg) {
                cxt?.showMessage('E', getErrorMessage(response));
            } else {
                onCreate(response);
            }
        },
        onError: (response: unknown) => {
            cxt?.showMessage('E', getErrorMessage(response));
        },
    });

    const [, loader] = useLoader([createMutation.status]);

    useEffect(() => {
        if (paymentsIds === null) {
            form.reset({
                childrenConcept: '',
                childrenUserId: '',
            });
        }
    }, [paymentsIds, form]);

    return (
        <SttDialog
            title="Cobrar pagos"
            open={paymentsIds !== null}
            onClose={onClose}
            content={
                <>
                    {loader}
                    <SttValidatedForm
                        form={form}
                        fields={[
                            {
                                type: 'textinput',
                                name: 'childrenConcept',
                                caption: t('Concept'),
                                rules: { required: true },
                            },
                        ]}
                    />
                </>
            }
            buttons={[
                {
                    type: 'save',
                    onClick: form.handleSubmit((formData) =>
                        createMutation.mutate({
                            params: {
                                childrenConcept: formData?.childrenConcept,
                                childrenUserId: userID,
                                childrenPaymentIds: paymentsIds!,
                            },
                        }),
                    ),
                },
            ]}
        />
    );
}

export default PayMultiplePaymentsDialog;
